import React, { useState, useEffect } from "react";
import {
	TextField,
	Button,
	Typography,
	Paper,
	Container,
	Grid,
	Box,
	Chip,
	LinearProgress,
	Card,
	CardContent,
	CardMedia,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Divider,
	Alert,
	Snackbar,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
} from "@mui/material";
import {
	Description,
	Send,
	Language,
	Wc,
	VideoCall,
	AutoAwesome,
	CheckCircle,
	MusicNote,
} from "@mui/icons-material";
import workflow from "../images/workflow.gif";
import Api from "../Api/Api.json";

const LanguageOptions = [
	"afrikaans",
	"albanian",
	"amharic",
	"arabic",
	"armenian",
	"assamese",
	"azerbaijani",
	"basque",
	"burmese",
	"bulgarian",
	"bengali",
	"bosnian",
	"catalan",
	"cantonese",
	"chinese",
	"czech",
	"croatian",
	"danish",
	"dutch",
	"english",
	"estonian",
	"finnish",
	"filipino",
	"french",
	"galician",
	"gujarati",
	"german",
	"greek",
	"georgian",
	"hebrew",
	"hindi",
	"hungarian",
	"indonesian",
	"icelandic",
	"irish",
	"italian",
	"inuktitut",
	"japanese",
	"javanese",
	"kazakh",
	"khmer",
	"kannada",
	"korean",
	"lao",
	"lithuanian",
	"latvian",
	"macedonian",
	"malayalam",
	"mongolian",
	"marathi",
	"malay",
	"maltese",
	"norwegian",
	"nepali",
	"odia",
	"punjabi",
	"polish",
	"pashto",
	"persian",
	"portuguese",
	"romanian",
	"russian",
	"sinhalese",
	"slovak",
	"slovenian",
	"somali",
	"spanish",
	"serbian",
	"sundanese",
	"swedish",
	"swahili",
	"tamil",
	"telugu",
	"thai",
	"turkish",
	"ukrainian",
	"urdu",
	"uzbek",
	"vietnamese",
	"welsh",
	"wu chinese",
	"zulu",
];

const BackgroundMusicCategories = [
	"Electronic",
	"Upbeat",
	"Beats",
	"Alternative Hip Hop",
	"Modern Classical",
	"Ambient",
	"Build Up Scenes",
	"Acoustic Group",
	"Solo Piano",
	"Corporate",
	"Solo Instruments"
];

const ProductVideo = () => {
	const [formData, setFormData] = useState({
		language: "english",
		gender: "female",
		features: "",
		backgroundMusic: "Upbeat"
	});
	const [productName, setProductName] = useState("Topic");
	const [generatedScript, setGeneratedScript] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [quota, setQuota] = useState(0);
	const [uploadedImages, setUploadedImages] = useState([]);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		getUserInfo();
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleProductNameChange = (e) => {
		setProductName(e.target.value);
	};

	const getUserInfo = async () => {
		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "getUserInfo",
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const user_info = await response.json();
			setQuota(user_info[0].quota);
			localStorage.setItem("user_info", JSON.stringify(user_info[0]));
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to fetch user information",
				severity: "error",
			});
		}
	};

	const handleSubmitScript = async () => {
		const num_words = generatedScript.split(" ").length;
		const topic_char_count = productName.length;

		if (
			productName === "" ||
			topic_char_count > 20 ||
			generatedScript === "" ||
			num_words < 30 ||
			num_words > 170 ||
			uploadedImages.length === 0
		) {
			setSnackbar({
				open: true,
				message:
					"Please ensure all fields are filled: product name, features, script (30-170 words), and at least one product image.",
				severity: "error",
			});
			return;
		}

		setIsLoading(true);

		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			// First, update the product.json configuration
			const productConfig = {
				render_type: "render_product_video",
				project_id: `product_${Date.now()}`,
				title: productName,
				resolution: {
					width: 1920,
					height: 1080
				},
				fps: 60,
				duration: 30.0,
				tracks: {
					product_animations: [
						{
							type: "product_image",
							src: uploadedImages.map(img => img.file),
							start: 0.0,
							duration: 30.0,
							position: {
								x: "center",
								y: "center"
							},
							scale: 1.0,
							opacity: 1.0,
							z_index: 1,
							effects_sequence: [
								{
									type: "zoom",
									start_time: 0.0,
									duration: 5.0,
									start_scale: 1.0,
									end_scale: 1.5,
									easing: "ease-in-out"
								},
								{
									type: "multiply",
									start_time: 5.0,
									duration: 5.0,
									copies: 4,
									pattern: "circle",
									spacing: 200,
									rotation: 360,
									easing: "ease-out"
								},
								{
									type: "particle_transform",
									start_time: 10.0,
									duration: 5.0,
									transform_type: "product_to_powder",
									particle_count: 1000,
									spread_radius: 300,
									particle_size: 3,
									color: "match_product"
								},
								{
									type: "particle_transform",
									start_time: 15.0,
									duration: 5.0,
									transform_type: "powder_to_product",
									gather_effect: "spiral",
									particle_count: 1000
								},
								{
									type: "bubble_effect",
									start_time: 20.0,
									duration: 5.0,
									bubble_count: 20,
									bubble_size: {
										min: 30,
										max: 80
									},
									bubble_opacity: 0.6,
									movement: "circular",
									color: "rainbow"
								}
							]
						}
					],
					audio: [
						{
							type: "voice_over",
							text: generatedScript,
							language: formData.language,
							gender: formData.gender,
							start: 0.0,
							volume: 1.0,
							fade_in: 0.5,
							fade_out: 0.5
						},
						{
							type: "background_music",
							category: formData.backgroundMusic,
							start: 0.0,
							volume: 0.3,
							fade_in: 2.0,
							fade_out: 2.0,
							loop: true
						}
					],
					overlay: [
						{
							type: "text",
							text: productName,
							start: 2.0,
							duration: 4.0,
							position: {
								x: "center",
								y: 100
							},
							font: {
								family: "Poppins",
								size: 48,
								color: "#ffffff",
								bold: true,
								shadow: {
									color: "rgba(0,0,0,0.5)",
									blur: 10,
									offset: {
										x: 2,
										y: 2
									}
								}
							},
							animation: {
								type: "fade_slide",
								direction: "up",
								duration: 1.0
							}
						},
						{
							type: "text",
							text: formData.features,
							start: 6.0,
							duration: 20.0,
							position: {
								x: "center",
								y: "bottom-100"
							},
							font: {
								family: "Poppins",
								size: 32,
								color: "#ffffff",
								weight: "500"
							},
							animation: {
								type: "typewriter",
								duration: 2.0
							}
						}
					]
				},
				background: {
					type: "gradient",
					gradient: {
						type: "linear",
						angle: 45,
						colors: [
							{"color": "#1a1a1a", "position": 0},
							{"color": "#4a4a4a", "position": 1}
						]
					}
				},
				export_settings: {
					format: "mp4",
					codec: "h264",
					quality: "high",
					bitrate: "8000k"
				}
			};

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url = Api.MAGICVERSE_USER_API;

			// Submit video generation request with product config
			const videoGenBody = {
				task: "submitProductVideoGenRequest",
				script: generatedScript,
				name: productName,
				language: formData.language,
				gender: formData.gender,
				features: formData.features,
				images: uploadedImages.map(img => img.base64),
				product_config: productConfig
			};
			console.log(videoGenBody);
			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(videoGenBody),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();
			console.log(data);
			if (data.code === 200) {
				setSnackbar({
					open: true,
					message:
						"Your video request has been submitted. You'll receive an email (usually 5-10 minutes) when the video is ready.",
					severity: "success",
				});
			} else {
				setSnackbar({
					open: true,
					message: "Something went wrong. Please try again later.",
					severity: "error",
				});
			}
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to submit video generation request",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleScriptChange = (e) => {
		setGeneratedScript(e.target.value);
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const handleImageUpload = (event) => {
		const files = Array.from(event.target.files);
		
		files.forEach(file => {
			const reader = new FileReader();
			reader.onloadend = () => {
				// Get base64 string without the prefix
				const base64String = reader.result.split(',')[1];
				setUploadedImages(prev => {
					const newState = [...prev, {
						file: file,
						preview: reader.result,
						base64: base64String,
						name: file.name
					}];
					return newState;
				});
			};
			reader.readAsDataURL(file);
		});
	};

	const handleRemoveImage = (indexToRemove) => {
		setUploadedImages(prev => prev.filter((_, index) => index !== indexToRemove));
	};

	// Calculate word count for display
	const wordCount = generatedScript ? generatedScript.split(" ").length : 0;
	const isWordCountValid = wordCount >= 30 && wordCount <= 170;

	const handleGenerateScript = async () => {
		if (!productName || productName === "Topic") {
			setSnackbar({
				open: true,
				message: "Please enter a valid product name",
				severity: "error",
			});
			return;
		}

		setIsLoading(true);

		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =Api.MAGICVERSE_USER_API;


			const body = {
				task: "getProductScriptFromFeatures",
				name: productName,
				features: formData.features,
				language:formData.language,
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			setGeneratedScript(data.script);
			setSnackbar({
				open: true,
				message: "Script generated successfully",
				severity: "success",
			});
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to generate script",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Container maxWidth="lg" className="py-8">
			<Paper elevation={3} className="rounded-lg overflow-hidden">
				<Box className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
					<Typography variant="h4" className="font-bold text-center">
						Create Product Video
					</Typography>
					<Typography
						variant="subtitle1"
						className="text-center mt-2"
					>
						Generate professional product videos with AI avatars
					</Typography>
				</Box>

				<CardContent className="p-6 bg-white">
					<Box className="mb-6">
						<Grid container spacing={2} className="mb-4">
							<Grid item xs={12} md={6}>
								<Chip
									icon={<AutoAwesome />}
									label={`Available Credits: ${quota}/20`}
									color="primary"
									variant="outlined"
									className="px-4 py-3"
								/>
							</Grid>
						</Grid>

						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<Description className="mr-2" />
							Product Name
						</Typography>

						<TextField
							fullWidth
							variant="outlined"
							placeholder="Enter a product name..."
							value={productName}
							onChange={handleProductNameChange}
							helperText="Keep your product name under 20 characters"
							className="mb-4"
						/>

						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center mt-4"
						>
							<VideoCall className="mr-2" />
							Product Features
						</Typography>

						<TextField
							fullWidth
							multiline
							rows={3}
							variant="outlined"
							name="features"
							value={formData.features}
							onChange={handleChange}
							placeholder="Enter key product features and benefits..."
							className="mb-4"
						/>

						<Paper elevation={3} className="p-6 mb-6">
							<Box className="flex items-center mb-4">
								<AutoAwesome className="text-orange-500 mr-2" />
								<Typography variant="h6">Upload Product Images</Typography>
							</Box>
							<Box className="flex flex-col items-center space-y-4">
								<input
									accept="image/*"
									style={{ display: 'none' }}
									id="product-image-upload"
									type="file"
									onChange={handleImageUpload}
									multiple
								/>
								<label htmlFor="product-image-upload">
									<Button
										variant="contained"
										component="span"
										color="primary"
										className="mb-4"
									>
										Choose Images
									</Button>
								</label>
								{uploadedImages.length > 0 && (
									<Box className="mt-4 w-full">
										<Typography variant="subtitle1" className="mb-2">
											Previews:
										</Typography>
										<Grid container spacing={2}>
											{uploadedImages.map((image, index) => (
												<Grid item xs={12} sm={6} md={4} key={index}>
													<Paper 
														elevation={2} 
														className="relative p-2 hover:shadow-md transition-shadow"
													>
														<img
															src={image.preview}
															alt={`Product preview ${index + 1}`}
															style={{
																width: '100%',
																height: '200px',
																objectFit: 'contain',
																borderRadius: '4px'
															}}
														/>
														<Box className="absolute top-2 right-2">
															<Button
																size="small"
																color="error"
																variant="contained"
																onClick={() => handleRemoveImage(index)}
																style={{ minWidth: '30px', padding: '4px' }}
															>
																×
															</Button>
														</Box>
														<Typography 
															variant="caption" 
															className="mt-1 block text-center text-gray-600"
															style={{ wordBreak: 'break-word' }}
														>
															{image.name}
														</Typography>
													</Paper>
												</Grid>
											))}
										</Grid>
									</Box>
								)}
							</Box>
						</Paper>

						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center mt-4"
						>
							<VideoCall className="mr-2" />
							Avatar and Audio Settings
						</Typography>

						<Grid container spacing={3} className="my-4">
							<Grid item xs={12} md={4}>
								<FormControl fullWidth variant="outlined">
									<InputLabel id="language-label">
										Language
									</InputLabel>
									<Select
										labelId="language-label"
										id="language"
										name="language"
										value={formData.language}
										onChange={handleChange}
										label="Language"
										startAdornment={
											<Language className="mr-2 text-gray-500" />
										}
									>
										{LanguageOptions.map((lang) => (
											<MenuItem key={lang} value={lang}>
												{lang.charAt(0).toUpperCase() +
													lang.slice(1)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} md={4}>
								<FormControl fullWidth variant="outlined">
									<InputLabel id="gender-label">
										Gender
									</InputLabel>
									<Select
										labelId="gender-label"
										id="gender"
										name="gender"
										value={formData.gender}
										onChange={handleChange}
										label="Gender"
										startAdornment={
											<Wc className="mr-2 text-gray-500" />
										}
									>
										<MenuItem value="male">Male</MenuItem>
										<MenuItem value="female">
											Female
										</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} md={4}>
								<FormControl fullWidth variant="outlined">
									<InputLabel id="background-music-label">
										Background Music
									</InputLabel>
									<Select
										labelId="background-music-label"
										id="backgroundMusic"
										name="backgroundMusic"
										value={formData.backgroundMusic}
										onChange={handleChange}
										label="Background Music"
										startAdornment={
											<MusicNote className="mr-2 text-gray-500" />
										}
									>
										{BackgroundMusicCategories.map((category) => (
											<MenuItem key={category} value={category}>
												{category}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
						</Grid>
					</Box>

					{isLoading && <LinearProgress className="mb-6" />}

					<Divider className="my-4" />

					<Box className="mt-6">
						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<Description className="mr-2" />
							Product Script
						</Typography>

						<Box className="my-6">
							<Button
								variant="contained"
								color="primary"
								onClick={handleGenerateScript}
								startIcon={<AutoAwesome />}
								disabled={isLoading}
								className="bg-blue-600 hover:bg-blue-700 font-bold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all"
							>
								Generate Script
							</Button>
						</Box>

						<TextField
							fullWidth
							multiline
							rows={10}
							variant="outlined"
							value={generatedScript}
							onChange={handleScriptChange}
							placeholder="Write your product script here..."
							className="mb-4"
						/>

						<Box className="flex justify-between items-center mb-4">
							<Typography
								variant="body2"
								className={`${
									isWordCountValid
										? "text-green-600"
										: "text-red-600"
								}`}
							>
								Word count: {wordCount}/170{" "}
								{!isWordCountValid && "(30-170 words required)"}
							</Typography>

							<Button
								variant="contained"
								color="warning"
								onClick={handleSubmitScript}
								startIcon={<Send />}
								disabled={isLoading || !isWordCountValid}
								className="bg-orange-600 hover:bg-orange-700 font-bold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all"
							>
								Generate Product Video
							</Button>
						</Box>
					</Box>

					<Divider className="my-4" />

					<Box className="mt-6">
						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<VideoCall className="mr-2" />
							Product Video Creation Process
						</Typography>

						<Box className="mt-4">
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={12} md={6}>
									<List className="pl-4">
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="warning"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Enter your product description" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="warning"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Select your preferred language and presenter" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="warning"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Write a compelling product script" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="warning"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Receive your product video within 5-10 minutes" />
										</ListItem>
									</List>
								</Grid>
								<Grid item xs={12} md={6}>
									<CardMedia
										component="img"
										image={workflow}
										alt="Product video creation workflow"
										className="w-full h-auto object-contain rounded-lg"
										style={{ maxWidth: "100%" }}
									/>
								</Grid>
							</Grid>
						</Box>
					</Box>
				</CardContent>
			</Paper>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default ProductVideo; 